<template>
    <div v-if="!isTablet() && visible" ref="zoomContainer" class="image-boomer">
        <img :alt="alt" :src="src" class="boom" :style="style"/>
    </div>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted, onBeforeUnmount, nextTick, useTemplateRef} from 'vue';
import {isTablet} from '@/plugins/Helpers';

interface Props {
    selector: string;
    src: string;
    alt?: string;
}

const {selector, src, alt = ''} = defineProps<Props>();

const imageContainerEl = ref<HTMLElement | null>(null);
const imageToZoomEl = ref<HTMLImageElement | null>(null);
const zoomContainerEl = useTemplateRef<HTMLDivElement>('zoomContainer');
const visible = ref(false);
const zoomHelperRectEl = ref(document.createElement('div'));
const zoomRatio = ref({x: 0, y: 0});
const zoomHelperRectSize = ref({width: 0, height: 0});
const originalImageSize = ref({width: 0, height: 0});
const imageToZoomSize = ref({width: 0, height: 0});
const imageToZoomOffset = ref({x: 0, y: 0});
const x = ref(0);
const y = ref(0);

/**
 * Loads original image to get the resolution
 */
const loadOriginalImage = () => {
    const image = new Image();
    image.onload = () => {
        originalImageSize.value.width = image.naturalWidth;
        originalImageSize.value.height = image.naturalHeight;
        computeZoomRatio();
    };
    image.src = src;
};

/**
 * Computes some important numbers, works only when zoomed image is visible
 */
const computeZoomRatio = () => {
    if (visible.value && imageContainerEl.value && imageToZoomEl.value && zoomContainerEl.value) {
        const containerEl = imageContainerEl.value;
        const imgEl = imageToZoomEl.value;
        const containerBoundingRect = containerEl.getBoundingClientRect();
        const imageBoundingRect = imgEl.getBoundingClientRect();
        const imageStyles = window.getComputedStyle(imgEl);
        const paddingLeft = parseFloat(imageStyles.paddingLeft) || 0;
        const paddingRight = parseFloat(imageStyles.paddingRight) || 0;
        const paddingTop = parseFloat(imageStyles.paddingTop) || 0;
        const paddingBottom = parseFloat(imageStyles.paddingBottom) || 0;
        const imageContentWidth = imgEl.clientWidth - paddingLeft - paddingRight;
        const imageContentHeight = imgEl.clientHeight - paddingTop - paddingBottom;

        if (
            originalImageSize.value.width === 0
            || originalImageSize.value.height === 0
            || imageContentWidth <= 0
            || imageContentHeight <= 0
        ) {
            return;
        }

        // Compute the displayed image bounds after object-fit: scale-down is applied.
        const imageScale = Math.min(
            1,
            imageContentWidth / originalImageSize.value.width,
            imageContentHeight / originalImageSize.value.height,
        );
        imageToZoomSize.value.width = originalImageSize.value.width * imageScale;
        imageToZoomSize.value.height = originalImageSize.value.height * imageScale;
        imageToZoomOffset.value.x = imageBoundingRect.left - containerBoundingRect.left
            + imgEl.clientLeft
            + paddingLeft
            + (imageContentWidth - imageToZoomSize.value.width) / 2;
        imageToZoomOffset.value.y = imageBoundingRect.top - containerBoundingRect.top
            + imgEl.clientTop
            + paddingTop
            + (imageContentHeight - imageToZoomSize.value.height) / 2;

        zoomRatio.value.x = originalImageSize.value.width / imageToZoomSize.value.width;
        zoomRatio.value.y = originalImageSize.value.height / imageToZoomSize.value.height;

        // The helper represents the exact source-image area visible in the zoom popup.
        zoomHelperRectSize.value.width = Math.min(
            imageToZoomSize.value.width,
            zoomContainerEl.value.clientWidth / zoomRatio.value.x,
        );
        zoomHelperRectSize.value.height = Math.min(
            imageToZoomSize.value.height,
            zoomContainerEl.value.clientHeight / zoomRatio.value.y,
        );

        zoomHelperRectEl.value.style.width = `${zoomHelperRectSize.value.width}px`;
        zoomHelperRectEl.value.style.height = `${zoomHelperRectSize.value.height}px`;
    }
};

/**
 * Shows the zoomed image
 */
const show = async () => {
    if (
        !visible.value
        && imageContainerEl.value
        && (
            originalImageSize.value.width > imageContainerEl.value.clientWidth
            || originalImageSize.value.height > imageContainerEl.value.clientHeight
        )
    ) {
        visible.value = true;
        zoomHelperRectEl.value.style.display = 'block';
        await nextTick();
        computeZoomRatio();
    }
};

/**
 * Hides the zoomed image
 */
const hide = () => {
    visible.value = false;
    zoomHelperRectEl.value.style.display = 'none';
};

/**
 * Computes the position of "helper" rectangle and zoomed image
 */
const onMouseMove = async (e: MouseEvent) => {
    await show();
    if (!imageContainerEl.value || !imageToZoomEl.value || !zoomContainerEl.value) {
        return;
    }

    const containerBoundingRect = imageContainerEl.value.getBoundingClientRect();
    const mouseOffsetX = e.clientX - containerBoundingRect.left;
    const mouseOffsetY = e.clientY - containerBoundingRect.top;
    let rectX = mouseOffsetX - zoomHelperRectSize.value.width / 2;
    let rectY = mouseOffsetY - zoomHelperRectSize.value.height / 2;

    rectX = Math.max(rectX, imageToZoomOffset.value.x);
    rectY = Math.max(rectY, imageToZoomOffset.value.y);
    rectX = Math.min(
        rectX,
        imageToZoomOffset.value.x + imageToZoomSize.value.width - zoomHelperRectSize.value.width,
    );
    rectY = Math.min(
        rectY,
        imageToZoomOffset.value.y + imageToZoomSize.value.height - zoomHelperRectSize.value.height,
    );

    zoomHelperRectEl.value.style.left = `${rectX}px`;
    zoomHelperRectEl.value.style.top = `${rectY}px`;

    x.value = (rectX - imageToZoomOffset.value.x) * zoomRatio.value.x;
    y.value = (rectY - imageToZoomOffset.value.y) * zoomRatio.value.y;
};

/**
 * style attribute of zoomed image
 */
const style = computed(() => {
    return {
        left: `${-x.value}px`,
        top: `${-y.value}px`,
    };
});

onMounted(() => {
    // add listeners
    imageContainerEl.value = document.querySelector<HTMLElement>(selector);
    if (!imageContainerEl.value) {
        return;
    }

    imageToZoomEl.value = imageContainerEl.value.querySelector<HTMLImageElement>('img');
    if (!imageToZoomEl.value) {
        return;
    }
    imageContainerEl.value.addEventListener('mouseenter', show);
    imageContainerEl.value.addEventListener('mousemove', onMouseMove);
    imageContainerEl.value.addEventListener('mouseleave', hide);
    imageContainerEl.value.style.position = 'relative';

    // add "helper" rectangle to original image
    zoomHelperRectEl.value.classList.add('boomer-rectangle');
    imageContainerEl.value.appendChild(zoomHelperRectEl.value);

    loadOriginalImage();
    window.addEventListener('resize', computeZoomRatio);
});

onBeforeUnmount(() => {
    // remove listeners
    if (imageContainerEl.value) {
        imageContainerEl.value.removeEventListener('mouseenter', show);
        imageContainerEl.value.removeEventListener('mousemove', onMouseMove);
        imageContainerEl.value.removeEventListener('mouseleave', hide);
    }
    window.removeEventListener('resize', computeZoomRatio);
});

watch(() => src, () => {
    loadOriginalImage();
});
</script>

<style lang="scss" scoped>
.image-boomer {
    position: relative;
    background-color: #F0F1F2;
    z-index: 2;
    border: 1px solid #CACBCC;
    overflow: hidden;
    pointer-events: none;
}

.boom {
    position: absolute;
    max-width: initial;
    object-fit: initial;
    max-height: initial;
}
</style>

<style lang="scss">
.boomer-rectangle {
    position: absolute;
    border: 1px solid #CACBCC;
    background-color: #F0F1F2;
    opacity: 0.5;
    display: none;
    pointer-events: none;
}
</style>
